import { CouponStorageData } from '../websocket/types';
import { CouponState } from '../../store/spirs/reducers/couponReducer';

const COUPON_STORAGE_KEY = 'coupon_pending_data';
const COUPON_INFO_STORAGE_KEY = 'coupon_info_pending_data';

export class CouponStorageService {

    public saveCoupon(coupon: CouponState) {
        try {
            const jsonData = JSON.stringify(coupon);
            localStorage.setItem(COUPON_INFO_STORAGE_KEY, jsonData);
        } catch (error) {
            console.error(
                'Ошибка сохранения данных купона в localStorage:',
                error
            );
        }
    }

    /**
     * Сохранение данных купона в localStorage
     */
    public saveCouponData(data: CouponStorageData): void {
        try {
            const jsonData = JSON.stringify(data);
            localStorage.setItem(COUPON_STORAGE_KEY, jsonData);
        } catch (error) {
            console.error(
                'Ошибка сохранения данных купона в localStorage:',
                error
            );
        }
    }

    /**
     * Получение данных купона из localStorage
     */
    public getCouponData(): CouponStorageData | null {
        try {
            const jsonData = localStorage.getItem(COUPON_STORAGE_KEY);
            if (!jsonData) {
                return null;
            }

            const data: CouponStorageData = JSON.parse(jsonData);
            return data;
        } catch (error) {
            console.error(
                'Ошибка чтения данных купона из localStorage:',
                error
            );
            return null;
        }
    }

    public getCoupon(): CouponState | null {
        try {
            const jsonData = localStorage.getItem(COUPON_INFO_STORAGE_KEY);
            if (!jsonData) {
                return null;
            }

            return JSON.parse(jsonData);
        } catch (error) {
            console.error(
                'Ошибка чтения данных купона из localStorage:',
                error
            );
            return null;
        }
    }

    /**
     * Удаление данных купона из localStorage
     */
    public removeCouponData(): void {
        try {
            localStorage.removeItem(COUPON_STORAGE_KEY);
        } catch (error) {
            console.error(
                'Ошибка удаления данных купона из localStorage:',
                error
            );
        }
    }

    public removeCoupon(): void {
        try {
            localStorage.removeItem(COUPON_INFO_STORAGE_KEY);
        } catch (error) {
            console.error(
                'Ошибка удаления данных купона из localStorage:',
                error
            );
        }
    }

    /**
     * Проверка наличия ожидающих данных купона
     */
    public hasPendingCoupon(): boolean {
        return this.getCouponData() !== null;
    }

    /**
     * Проверка, не истек ли таймаут для ожидающего купона
     */
    public isPendingCouponValid(): boolean {
        const data = this.getCouponData();
        if (!data) {
            return false;
        }

        const now = Date.now();
        const timeElapsed = now - data.receivedAt;
        const timeoutMs = data.timeout * 1000; // Преобразование секунд в миллисекунды

        return timeElapsed < timeoutMs;
    }

    /**
     * Получение оставшегося времени для ожидающего купона в секундах
     */
    public getRemainingTime(): number {
        const data = this.getCouponData();
        if (!data) {
            return 0;
        }

        const now = Date.now();
        const timeElapsed = now - data.receivedAt;
        const timeoutMs = data.timeout * 1000;
        const remainingMs = timeoutMs - timeElapsed;

        return Math.max(0, Math.ceil(remainingMs / 1000));
    }

    /**
     * Альтернативный метод с использованием cookies (если localStorage недоступен)
     */
    public saveCouponDataToCookie(data: CouponStorageData): void {
        try {
            const jsonData = JSON.stringify(data);
            const expiresInDays = 1; // Истекает через 1 день
            const expirationDate = new Date();
            expirationDate.setTime(
                expirationDate.getTime() + expiresInDays * 24 * 60 * 60 * 1000
            );

            document.cookie = `${COUPON_STORAGE_KEY}=${encodeURIComponent(
                jsonData
            )}; expires=${expirationDate.toUTCString()}; path=/`;
        } catch (error) {
            console.error('Ошибка сохранения данных купона в cookie:', error);
        }
    }

    /**
     * Получение данных купона из cookie
     */
    public getCouponDataFromCookie(): CouponStorageData | null {
        try {
            const name = COUPON_STORAGE_KEY + '=';
            const decodedCookie = decodeURIComponent(document.cookie);
            const cookieArray = decodedCookie.split(';');

            for (let cookie of cookieArray) {
                cookie = cookie.trim();
                if (cookie.indexOf(name) === 0) {
                    const jsonData = cookie.substring(name.length);
                    return JSON.parse(jsonData);
                }
            }
            return null;
        } catch (error) {
            console.error('Ошибка чтения данных купона из cookie:', error);
            return null;
        }
    }

    /**
     * Удаление данных купона из cookie
     */
    public removeCouponDataFromCookie(): void {
        try {
            document.cookie = `${COUPON_STORAGE_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        } catch (error) {
            console.error('Ошибка удаления данных купона из cookie:', error);
        }
    }

    /**
     * Универсальный метод сохранения (сначала пробует localStorage, затем fallback на cookies)
     */
    public save(data: CouponStorageData): void {
        try {
            this.saveCouponData(data);
        } catch (error) {
            console.warn('localStorage недоступен, переключение на cookies');
            this.saveCouponDataToCookie(data);
        }
    }

    /**
     * Универсальный метод получения (сначала пробует localStorage, затем fallback на cookies)
     */
    public get(): CouponStorageData | null {
        try {
            return this.getCouponData();
        } catch (error) {
            console.warn('localStorage недоступен, пробуем cookies');
            return this.getCouponDataFromCookie();
        }
    }

    /**
     * Универсальный метод удаления (удаляет из localStorage и cookies)
     */
    public remove(): void {
        console.log(new Date(), '[COUPON WS] remove coupon')
        this.removeCouponData();
        this.removeCouponDataFromCookie();
        this.removeCoupon();
    }
}

// Синглтон экземпляр
export const couponStorageService = new CouponStorageService();
